import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import { useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';

import { AdminSearchInsideDocument, useSearchInsideDocumentLazy } from '@SERVICES';
import { containsSearchTerm, extractContentSnippets } from '@HELPERS/textHighlighting';

import ResultSetWrapper from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/ResultSetWrapper';
import SearchResultCard from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/SearchResultCard';
import ContentMatch from '@COMPONENTS/HEADERS/breadcrumbs/AdminBreadclumbs/components/ContentMatch';

import styles from './DocumentResults.module.scss';

const cx: CX = classnames.bind(styles);

function DocumentResults(props: Props) {
    const {
        id,
        q,
        setLoading,
        isFocused,
        setFocused,
        searchValue,
        onSearchChange,
        isSearchLoading,
        searchPlaceholder,
    } = props;

    const [
        searchInsideDocument,
        {
            data, isLoading, requestId,
        },
    ] = useSearchInsideDocumentLazy();

    const navigate = useNavigate();

    const [results, setResults] = useState<AdminSearchInsideDocument | undefined>(undefined);

    const showResultSet = (
        q.length > 2
        && results !== undefined
        && isFocused
    );

    useLayoutEffect(() => {
        setLoading(isLoading);
    }, [setLoading, isLoading]);

    useLayoutEffect(() => {
        if (data) {
            setResults(data);
        }
    }, [data, requestId]);

    useLayoutEffect(() => {
        if (q.length <= 2) {
            setResults(undefined);
        }
    }, [q]);

    useEffect(() => {
        if (q.trim().length > 2) {
            const result = searchInsideDocument({
                id,
                q,
            });

            return () => {
                result.abort();
            };
        }
    }, [id, q, searchInsideDocument]);

    if (!showResultSet) return null;

    const hasResults = results?.data.chapters.length > 0;

    function renderSearchResults() {
        if (!results) return null;

        const searchResults: JSX.Element[] = [];

        // Check document name match
        if (containsSearchTerm(results.data.name, q)) {
            searchResults.push(
                <SearchResultCard
                    key={`document-${results.data.id}`}
                    title={results.data.name}
                    type="document"
                    searchTerm={q}
                    onClick={() => navigate(ROUTES.admin.documents.id.get(results.data.id))}
                />
            );
        }

        // Process chapters
        results.data.chapters.forEach((chapter) => {
            const chapterNameMatch = containsSearchTerm(chapter.name, q);
            const chapterContentSnippets = chapter.body ? extractContentSnippets(chapter.body, q) : [];

            // Show name match for chapter
            if (chapterNameMatch) {
                searchResults.push(
                    <SearchResultCard
                        key={`chapter-name-${chapter.id}`}
                        title={chapter.name}
                        type="chapter"
                        searchTerm={q}
                        onClick={() => navigate(ROUTES.admin.chapters.id.get(chapter.id))}
                    />
                );
            }

            // Show content matches for chapter
            if (chapterContentSnippets.length > 0) {
                searchResults.push(
                    <ContentMatch
                        key={`chapter-content-${chapter.id}`}
                        title={chapter.name}
                        type="chapter"
                        snippets={chapterContentSnippets}
                        onClick={() => navigate(ROUTES.admin.chapters.id.get(chapter.id))}
                        metadata="Introduction text"
                    />
                );
            }

            // Process sections
            chapter.sections.forEach((section) => {
                const sectionNameMatch = containsSearchTerm(section.name, q);
                const sectionContentSnippets = section.body ? extractContentSnippets(section.body, q) : [];

                // Show name match for section
                if (sectionNameMatch) {
                    searchResults.push(
                        <SearchResultCard
                            key={`section-name-${section.id}`}
                            title={section.name}
                            type="section"
                            searchTerm={q}
                            onClick={() => navigate(ROUTES.admin.sections.id.get(section.id))}
                            metadata={`in ${chapter.name}`}
                        />
                    );
                }

                // Show content matches for section
                if (sectionContentSnippets.length > 0) {
                    searchResults.push(
                        <ContentMatch
                            key={`section-content-${section.id}`}
                            title={section.name}
                            type="section"
                            snippets={sectionContentSnippets}
                            onClick={() => navigate(ROUTES.admin.sections.id.get(section.id))}
                            metadata={`in ${chapter.name} • Introduction text`}
                        />
                    );
                }

                // Process variants
                section.variants.forEach((variant) => {
                    const variantNameMatch = containsSearchTerm(variant.name, q);
                    const variantContentSnippets = extractContentSnippets(variant.body, q);

                    // Show name match for variant
                    if (variantNameMatch) {
                        searchResults.push(
                            <SearchResultCard
                                key={`variant-name-${variant.id}`}
                                title={variant.name}
                                type="variant"
                                searchTerm={q}
                                onClick={() => navigate(ROUTES.admin.sections.id.get(section.id, variant.id))}
                                metadata={`in ${chapter.name} → ${section.name}`}
                            />
                        );
                    }

                    // Show content matches for variant
                    if (variantContentSnippets.length > 0) {
                        searchResults.push(
                            <ContentMatch
                                key={`variant-content-${variant.id}`}
                                title={variant.name}
                                type="variant"
                                snippets={variantContentSnippets}
                                onClick={() => navigate(ROUTES.admin.sections.id.get(section.id, variant.id))}
                                metadata={`in ${chapter.name} → ${section.name}`}
                            />
                        );
                    }
                });
            });
        });

        return searchResults;
    }

    const searchResults = renderSearchResults();

    return (
        <ResultSetWrapper
            setFocused={setFocused}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            isSearchLoading={isSearchLoading}
            searchPlaceholder={searchPlaceholder}
        >
            <div className={cx('document-results')}>
                {searchResults && searchResults.length > 0 ? (
                    <div className={cx('results-list')}>
                        {searchResults}
                    </div>
                ) : (
                    <div className={cx('empty-state')}>
                        <div className={cx('empty-icon')}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="21 21l-4.35-4.35" />
                                <path d="11 8v6" />
                                <path d="8 11h6" />
                            </svg>
                        </div>
                        <div className={cx('empty-title')}>
                            No results found
                        </div>
                        <div className={cx('empty-description')}>
                            We couldn&apos;t find any content matching &quot;{searchValue}&quot;. Try adjusting your search terms or browse the document structure instead.
                        </div>
                    </div>
                )}
            </div>
        </ResultSetWrapper>
    );
}

DocumentResults.propTypes = {
    id: PropTypes.number.isRequired,
    q: PropTypes.string.isRequired,
    setLoading: PropTypes.func.isRequired,
    isFocused: PropTypes.bool.isRequired,
    setFocused: PropTypes.func.isRequired,
    searchValue: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    isSearchLoading: PropTypes.bool.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
};

type Props = InferProps<typeof DocumentResults.propTypes>;

export default DocumentResults;
